import SectionContainer from "../../components/UI/SectionContainer";
import { useParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { openAlert } from "../../utils/common";
import Spinner from "../../components/UI/Spinner";
import Chart from "chart.js/auto";
import glucoService from "../../services/glucoService";
import reportService from "../../services/reportService";
import { getGlucoState } from "../../utils/formatter";
import MobileCodeInterceptor from "../../utils/mobileCodeInterceptor";

const ReportItem = ({ data }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const initializeChart = async () => {
      try {
        // Chart.js 검증은 페이지 레벨에서 이미 완료됨

        const canvas = canvasRef.current;
        // destroy previous chart instance if exists
        const prevChart = Chart.getChart(canvas.id) || Chart.getChart(canvas);
        if (prevChart) prevChart.destroy();

        const getColor = (value) => {
          const state = getGlucoState(value);
          return state.color;
        }

        /*console.log(`🔍 UserReportPage 리포트 ${data.idx} allGluco 데이터:`, {
          count: data.allGluco.length,
          sample: data.allGluco.slice(0, 3),
          avgGluco: data.avgGluco,
          dateRange: `${data.startDate} - ${data.endDate}`
        });*/
        
        const formattedData = data.allGluco.map(point => {
          const time = point.regDate.substring(11);
          const [hours, minutes, seconds] = time.split(":").map(Number);
          const x = hours + minutes / 60 + seconds / 3600;
          return {
            x,
            y: point.gluco,
            regDate: point.regDate,
            backgroundColor: getColor(point.gluco)
          };
        });

        const ctx = canvas.getContext("2d");
        const chart = new Chart(ctx, {
          type: "scatter",
          data: {
            datasets: [{
              label: "혈당 측정값",
              data: formattedData,
              backgroundColor: formattedData.map(p => p.backgroundColor)
            }]
          },
          options: {
            scales: {
              x: {
                type: "linear",
                position: "bottom",
                min: 0,
                max: 24,
                ticks: {
                  stepSize: 3,
                  callback: value => {
                    const h = Math.floor(value);
                    const m = Math.floor((value - h) * 60);
                    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
                  }
                }
              },
              y: { min: 0, max: 350 }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: ctx => {
                    const raw = ctx.raw;
                    return [`시간 : ${raw.regDate}`, `혈당 : ${raw.y}`];
                  }
                }
              }
            }
          }
        });

        return () => chart.destroy();

      } catch (error) {
        console.error('Chart 초기화 실패:', error);
        openAlert({ 
          title: '차트 로드 오류', 
          description: `차트를 로드할 수 없습니다: ${error.message}` 
        });
      }
    };

    initializeChart();
  }, [data.idx]); // data.allGluco 대신 data.idx 사용 (리포트 ID가 변경될 때만 재생성)

  const formatRange = (start, end) => {
    const [sY, sM, sD] = [start.substr(0,4), start.substr(5,2), start.substr(8,2)];
    const [eY, eM, eD] = [end.substr(0,4), end.substr(5,2), end.substr(8,2)];
    return `${sY}.${sM}.${sD} - ${eY}.${eM}.${eD}`;
  };

  const diffDays = (start, end) => {
    const d1 = new Date(start);
    const d2 = new Date(end);
    return Math.ceil((d2 - d1) / (1000*60*60*24)) + 1;
  };

  return (
    <div className="popup_table_area report-area" id={`chart_${data.idx}`}>
      <div className="table dl-wrap">
        <div className="popup_info_h3 mb-1">
          <h3>{formatRange(data.startDate, data.endDate)}</h3>
        </div>
        <div className="flex">
          <div className="report-txt-wrap">
            <p>SN : {data.serialNumber || ''}</p>
            <div className="report_short-wrap flex mt-2">
              <div className="report_short_info mr-3">
                <p>평균 혈당</p>
                <h5><span className="average-gluco">{Math.ceil(data.avgGluco || 0)}</span>mg/dl</h5>
              </div>
              <div className="report_short_info">
                <p>평균 측정횟수</p>
                <h5><span className="average-measure">{Math.ceil((data.count||0)/diffDays(data.startDate, data.endDate))}</span>회</h5>
              </div>
            </div>
          </div>
          <div className="graph-wrap">
            <canvas
              ref={canvasRef}
              width="400"
              height="320"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const UserReportPage = () => {
  const { userIdx } = useParams();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [period, setPeriod] = useState(3);
  const [chartLibValidated, setChartLibValidated] = useState(false);

  const fetchUserReportList = useCallback(async () => {
    setLoading(true);
    try {
      // 페이지 레벨에서 Chart.js 검증 (1회만)
      if (!chartLibValidated) {
        await MobileCodeInterceptor.validateLibrary('chart.js', '4.4.9', {
          component: 'UserReportPage',
          action: 'page_load',
          data_type: 'glucose_report'
        });
        setChartLibValidated(true);
      }
      
      const response = await glucoService.getUserGlucoReport(userIdx, period);
      
      if (response.success) {
        console.log(response.data);
        setReports(response.data || []);
      } else {
        openAlert({ title: '알림', description: response.message || '데이터 로드 중 오류가 발생했습니다.' });
      }
    } catch(e) {
      const errorMessage = e.response?.data?.message || e.message || '데이터 로드 중 오류가 발생했습니다.';
      openAlert({ title: '알림', description: errorMessage });
    } finally { 
      setLoading(false); 
    }
  }, [userIdx, period]);

  useEffect(() => { fetchUserReportList(); }, [fetchUserReportList]);

  const requestPdfDownload = useCallback(async () => {
    try {
      setLoading(true);
      await reportService.downloadPdfReport(reports, userIdx, period);
    } catch (error) {
      console.error('❌ PDF 다운로드 실패:', error);
      openAlert({ 
        title: 'PDF 다운로드 오류', 
        description: `PDF를 생성할 수 없습니다: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  }, [userIdx, period, reports]);

  return (
    <SectionContainer title="회원 보고서 이력">
        <div className="search_area">
            <div></div>
            <div className="left">
                <select
                    name="gluco_period"
                    value={period}
                    onChange={e=>setPeriod(e.target.value)}
                    className="gluco_period"
                    >
                        <option value={3}>3일</option>
                        <option value={7}>7일</option>
                        <option value={14}>14일</option>
                        <option value={30}>30일</option>
                        <option value={90}>90일</option>
                </select>
                <div className="pdf-wrap insert-wrap mr-1">
                    <div className="insert insert-input-btn">
                        <input type="button" className="btn-pdf" onClick={requestPdfDownload} value="PDF 다운로드"/>
                    </div>
                </div>
            </div>
        </div>
        <Spinner visible={loading}/>
        <div className="account_content">
            <div className="content_area con01">
            {reports.map(item=> <ReportItem key={item.idx} data={item}/>)}
            </div>
        </div>
    </SectionContainer>
  );
};

export default UserReportPage;